import React, { useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrentUser } from "../../../redux/actions";
import { Form, Button, message } from "antd";
import CommonCheckbox from "../../global/checkbox";
import LocalStorageService from "../../../utils/localStorageServices";
import { useGenericMutation } from "../../../queries/use-generic-mutation";
import sidebarImage from "../../../assets/login-sidebar.webp";
import SOURCES from "../../../utils/constants";
import CommonForm from "../../global/form";
import Spinner from "../../global/spinner";

const localStorageServices = LocalStorageService.getService();

const EnhancedLogin = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    // Check for existing token
    const currentUser = localStorageServices.getCurrentUser();
    const token = currentUser?.token;

    useEffect(() => {
        // If token is invalid/undefined, clear it
        if (currentUser === "undefined" || currentUser === null) {
            localStorageServices.clearToken();
        }
    }, [currentUser]);

    // Redirect if already logged in
    if (token && token !== null) {
        history.push('/dashboard');
    }

    const loginMutation = useGenericMutation({
        endpoint: `/user/signin`,
    });

    const handleFinish = async (values) => {
        try {
            const result = await loginMutation.mutateAsync({
                email: values.email,
                password: values.password,
            });

            if (result?.data?.token) {
                message.success("Login successful");
                localStorageServices.setCurrentUser(result?.data);
                dispatch(setCurrentUser(result));
                const userRole = result?.data?.user?.userRole
                const redirectTo = userRole === SOURCES.USER_ROLE_AFFILIATE ? "/dashboard-affiliate" : "/dashboard"
                window.location.href = redirectTo;
            }
        } catch (err) {
            console.error(err)
        }
    };

    const formData = [
        {
            label: "Email",
            name: "email",
            type: "email",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Enter your email",
            span: 24,
            message: "Email is required",
        },
        {
            label: "Password",
            name: "password",
            type: "password",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Enter your password",
            span: 24,
            message: "Password is required",
            isLogIn: true,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: (
                <div className="flex justify-between items-center w-full mb-4">
                    <Form.Item name="rememberMe" valuePropName="checked" noStyle initialValue={false}>
                        <CommonCheckbox size="lg" className="text-gray-600 ml-3">Remember Me</CommonCheckbox>
                    </Form.Item>
                    <Link to="/forgotpassword" className="text-sm font-semibold text-primary hover:text-primary/80">
                        Forgot Password?
                    </Link>
                </div>
            )
        }
    ];

    return (
        <div className="flex min-h-screen">
            {/* Left Side - Image & Slogan */}
            <div className="hidden lg:flex w-1/2 relative bg-gray-900 items-center justify-center overflow-hidden">
                <img
                    src={sidebarImage}
                    alt="Login Background"
                    className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Logo on Sidebar */}
                <div className="absolute top-8 left-8 z-30">
                    <h1 className="text-4xl font-bold tracking-tighter m-0">
                        <span className="text-primary">Leads</span>
                        <span className="text-white">Import</span>
                    </h1>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-8 lg:p-16">
                <div className="w-full max-w-md space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold text-gray-900">Welcome Back</h2>
                        <p className="mt-2 text-gray-500">Please sign in to your dashboard</p>
                    </div>

                    <div className="bg-white">
                        <CommonForm
                            form={form}
                            data={formData}
                            onFinish={handleFinish}
                            isInternalSubmitt={false}
                        />

                        <Button
                            type="primary"
                            onClick={() => form.submit()}
                            className="w-full h-12 text-base font-bold rounded-xl shadow-lg shadow-primary/25 !bg-primary hover:!bg-primary/90 border-none gap-x-2"
                        >
                            {loginMutation.isPending && <Spinner className="!border-white" />}Sign In
                        </Button>

                        <p className="text-center mt-8 text-gray-600">
                            Don't have an account?{" "}
                            <Link to="/signup" className="font-bold text-primary hover:text-primary/80">
                                Sign Up Now
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedLogin;
